import * as React from 'react';
import { Tag, EyeOff, Trash2, CheckSquare, Square, MinusSquare, HandCoins, LayoutGrid, MoreHorizontal, Images, Sparkles, CheckCircle2, Copy } from 'lucide-react';
import { EbayBag } from '@/components/icons/EbayBag';
import { Button } from '@/components/ui/button';
import { ActionMenu, type ActionMenuItem } from '@/components/ui/ActionMenu';
import { cn } from '@/lib/utils';

export interface SelectionSummary {
  total: number;
  listed: number;
  unlisted: number;
  /** Items eligible to flip from unlisted → listed (priced OR taking offers). */
  pricedUnlisted: number;
  /** Selected items currently with is_negotiable = true (any visibility). */
  acceptingOffers: number;
  /** Selected items with a pending Collectr import price ready to apply. */
  collectrPriceable?: number;
}

interface SelectionActionBarProps {
  selectedCount: number;
  totalCount: number;
  onCancel: () => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onListSelected: () => void;
  onPrivateSelected: () => void;
  onAcceptOffersSelected?: () => void;
  onSaveToMenu?: () => void;
  onAddStockPhotos?: () => void;
  onListOnEbay?: () => void;
  onSuggestPrices?: () => void;
  onMarkSold?: () => void;
  onDuplicateSelected?: () => void;
  onDeleteSelected: () => void;

  isProcessing?: boolean;
  selectionSummary?: SelectionSummary;
}

/**
 * Mobile bulk-selection drawer (v7).
 * Real bottom-sheet feel: grab handle + swipe-down dismiss, no Done button,
 * non-modal so users can still tap rows to toggle selection.
 * Desktop is gated out (`lg:hidden`).
 */
export function SelectionActionBar({
  selectedCount,
  totalCount,
  onCancel,
  onSelectAll,
  onDeselectAll,
  onListSelected,
  onPrivateSelected,
  onAcceptOffersSelected,
  onSaveToMenu,
  onAddStockPhotos,
  onListOnEbay,
  onSuggestPrices,
  onMarkSold,
  onDuplicateSelected,
  onDeleteSelected,
  isProcessing = false,
  selectionSummary,
}: SelectionActionBarProps) {
  // Auto-exit selection mode when the underlying inventory is empty — otherwise
  // the drawer is stuck at 0 / 0 with nothing to act on.
  React.useEffect(() => {
    if (totalCount === 0) onCancel();
  }, [totalCount, onCancel]);

  const allSelected = selectedCount > 0 && selectedCount === totalCount;
  const someSelected = selectedCount > 0 && selectedCount < totalCount;
  const hasSelection = selectedCount > 0;

  const summary = selectionSummary ?? { total: selectedCount, listed: 0, unlisted: selectedCount, pricedUnlisted: 0, acceptingOffers: 0 };
  const allListed = hasSelection && summary.listed === summary.total;
  const allUnlisted = hasSelection && summary.unlisted === summary.total;
  const isMixed = hasSelection && !allListed && !allUnlisted;
  const offersMissing = Math.max(0, summary.total - summary.acceptingOffers);
  const showOffersBtn = hasSelection && !!onAcceptOffersSelected && offersMissing > 0;

  const showListBtn = hasSelection && !allListed;
  const showUnlistBtn = hasSelection && !allUnlisted;
  const listDisabled = isProcessing || summary.unlisted === 0;

  const CountIcon = allSelected ? CheckSquare : someSelected ? MinusSquare : Square;
  const onCountTap = () => (allSelected ? onDeselectAll() : onSelectAll());

  // --- Swipe-down-to-dismiss (touch only) ---
  const sheetRef = React.useRef<HTMLDivElement | null>(null);
  const drag = React.useRef({ y: 0, t: 0, dy: 0, dragging: false });

  const onGrabPointerDown = (e: React.PointerEvent) => {
    // Touch + pen only — mouse stays click-only to avoid swallowing desktop hover users.
    if (e.pointerType === 'mouse') return;
    const el = sheetRef.current;
    if (!el) return;
    drag.current = { y: e.clientY, t: performance.now(), dy: 0, dragging: false };
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);

    const onMove = (ev: PointerEvent) => {
      const dy = ev.clientY - drag.current.y;
      if (dy <= 0 && !drag.current.dragging) return;
      drag.current.dragging = true;
      drag.current.dy = Math.max(0, dy);
      el.style.transition = 'none';
      el.style.transform = `translateY(${drag.current.dy}px)`;
    };
    const onUp = () => {
      const dt = Math.max(1, performance.now() - drag.current.t);
      const velocity = drag.current.dy / dt;
      const shouldClose = drag.current.dy > 80 || velocity > 0.5;
      el.style.transition = '';
      el.style.transform = '';
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      const wasDragging = drag.current.dragging;
      drag.current = { y: 0, t: 0, dy: 0, dragging: false };
      if (wasDragging && shouldClose) onCancel();
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerup', onUp, { passive: true });
    window.addEventListener('pointercancel', onUp, { passive: true });
  };

  // Outside-tap-to-dismiss: close when tapping a non-interactive background area.
  // Taps on cards, buttons, checkboxes, links, inputs stay live so users can keep
  // toggling selection with the drawer open.
  React.useEffect(() => {
    const onDocPointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const sheet = sheetRef.current;
      if (sheet && sheet.contains(target)) return;
      // If any dialog/sheet/popover is open above us, defer to it — a click there
      // is not a "tap outside to dismiss selection" gesture.
      if (
        document.querySelector(
          '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [data-radix-popper-content-wrapper]'
        )
      ) {
        return;
      }
      // Ignore taps on any interactive/selectable surface.
      if (
        target.closest(
          'button, a, input, label, select, textarea, [role="button"], [role="checkbox"], [role="menuitem"], [role="dialog"], [data-inventory-card], [data-radix-popper-content-wrapper]'
        )
      ) {
        return;
      }
      onCancel();
    };
    document.addEventListener('pointerdown', onDocPointerDown, true);
    return () => document.removeEventListener('pointerdown', onDocPointerDown, true);
  }, [onCancel]);


  if (totalCount === 0) return null;

  const menuItems: ActionMenuItem[] = [];
  if (hasSelection && showListBtn) {
    menuItems.push({
      key: 'list',
      label: `List for sale${isMixed && summary.pricedUnlisted ? ` ${summary.pricedUnlisted}` : ''}`,
      icon: <Tag className="w-5 h-5 text-active-gold" />,
      onSelect: onListSelected,
      disabled: listDisabled,
    });
  }
  if (hasSelection && showListBtn && showUnlistBtn) {
    menuItems.push({
      key: 'unlist',
      label: `Unlist${isMixed ? ` ${summary.listed}` : ''}`,
      icon: <EyeOff className="w-5 h-5" />,
      onSelect: onPrivateSelected,
      disabled: isProcessing,
    });
  }
  if (hasSelection && showOffersBtn) {
    menuItems.push({
      key: 'offers',
      label: `Accept offers${offersMissing < summary.total ? ` (${offersMissing})` : ''}`,
      icon: <HandCoins className="w-5 h-5 text-active-gold" />,
      onSelect: onAcceptOffersSelected!,
      disabled: isProcessing,
    });
  }
  // Scope-agnostic: works with a selection OR against all filtered items missing photos.
  if (onAddStockPhotos) {
    menuItems.push({
      key: 'stock-photos',
      label: 'Add stock photos',
      icon: <Images className="w-5 h-5 text-active-gold" />,
      onSelect: onAddStockPhotos,
      disabled: isProcessing,
    });
  }
  if (hasSelection && onSaveToMenu) {
    menuItems.push({
      key: 'menu',
      label: 'Add to menu…',
      icon: <LayoutGrid className="w-5 h-5 text-active-gold" />,
      onSelect: onSaveToMenu,
      disabled: isProcessing,
    });
  }
  if (hasSelection && onListOnEbay) {
    menuItems.push({
      key: 'ebay',
      label: 'List on eBay',
      icon: <EbayBag className="h-5 w-5" />,
      onSelect: onListOnEbay,
      disabled: isProcessing,
    });
  }
  if (hasSelection && onSuggestPrices) {
    menuItems.push({
      key: 'suggest',
      label: 'Suggest prices',
      icon: <Sparkles className="w-5 h-5 text-active-gold" />,
      onSelect: onSuggestPrices,
      disabled: isProcessing,
    });
  }
  if (hasSelection && onDuplicateSelected) {
    menuItems.push({
      key: 'duplicate',
      label: 'Duplicate',
      icon: <Copy className="w-5 h-5 text-active-gold" />,
      onSelect: onDuplicateSelected,
      disabled: isProcessing,
    });
  }
  if (hasSelection && onMarkSold) {
    menuItems.push({
      key: 'mark-sold',
      label: 'Mark as sold',
      icon: <CheckCircle2 className="w-5 h-5 text-active-gold" />,
      onSelect: onMarkSold,
      disabled: isProcessing,
    });
  }
  if (hasSelection) {
    menuItems.push({
      key: 'delete',
      label: 'Delete',
      icon: <Trash2 className="w-5 h-5" />,
      onSelect: onDeleteSelected,
      disabled: isProcessing,
      destructive: true,
      separatorBefore: menuItems.length > 0,
    });
  }

  const moreActionsTrigger = (
    <Button
      variant="outline"
      size="icon"
      className="h-8 w-8 shrink-0 rounded-full bg-white/[0.05] hover:bg-white/[0.08] border-white/10"
      aria-label="More actions"
    >
      <MoreHorizontal className="w-4 h-4" />
    </Button>
  );

  return (
    <div
      ref={sheetRef}
      className={cn(
			'lg:hidden fixed inset-x-0 bottom-0 z-[45]',
        'bg-chrome rounded-t-2xl',
        'border-t border-white/[0.08]',
        'shadow-[0_-12px_32px_-8px_rgba(0,0,0,0.6)]',
        'animate-in slide-in-from-bottom duration-200 ease-out',
        'will-change-transform'
      )}
      style={{
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 10px)',
      }}
      role="dialog"
      aria-label="Bulk selection actions"
    >
      {/* Grab handle */}
      <div
        onPointerDown={onGrabPointerDown}
        style={{ touchAction: 'none' }}
        className="flex h-7 items-center justify-center cursor-grab active:cursor-grabbing select-none [-webkit-tap-highlight-color:transparent]"
        aria-hidden
      >
        <div className="h-1.5 w-10 rounded-full bg-white/25 pointer-events-none" />
      </div>

      {/* Header row: count + inline status + more actions */}
      <div className="flex items-center gap-2 px-4 h-9">
        <button
          onClick={onCountTap}
          className="flex items-center gap-2 -ml-1 px-1.5 h-8 rounded-md hover:bg-white/[0.04] active:bg-white/[0.08] transition-colors shrink-0"
          aria-label={allSelected ? 'Deselect all' : 'Select all'}
        >
          <CountIcon className={cn('w-4 h-4', allSelected ? 'text-active-gold' : 'text-muted-foreground')} />
          <span className="text-sm font-semibold tabular-nums text-foreground">
            {selectedCount}
            <span className="text-muted-foreground font-normal"> / {totalCount}</span>
          </span>
        </button>
        {menuItems.length > 0 && (
          <div className="ml-auto">
            <ActionMenu mobileTitle="More actions" items={menuItems} trigger={moreActionsTrigger} />
          </div>
        )}
      </div>


      {/* Primary row — List/Unlist */}
      <div className="flex items-center gap-2 px-4 mt-2">
        {hasSelection && showListBtn && (
          <Button
            onClick={onListSelected}
            disabled={listDisabled}
            className={cn(
              'h-11 gap-2 font-semibold px-3 flex-1 min-w-0',
              'bg-active-gold/95 hover:bg-active-gold text-black border-0'
            )}
          >
            <Tag className="w-4 h-4 shrink-0" />
            <span className="truncate">List{isMixed && summary.pricedUnlisted ? ` ${summary.pricedUnlisted}` : ''}</span>
          </Button>
        )}
        {hasSelection && !showListBtn && showUnlistBtn && (
          <Button
            onClick={onPrivateSelected}
            disabled={isProcessing}
            variant="outline"
            className="h-11 gap-2 px-3 flex-1 min-w-0 bg-white/[0.05] hover:bg-white/[0.08] border-white/10"
          >
            <EyeOff className="w-4 h-4 shrink-0 text-muted-foreground" />
            <span className="truncate font-medium">Unlist{isMixed ? ` ${summary.listed}` : ''}</span>
          </Button>
        )}
      </div>

    </div>
  );
}
